"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useEffect, useRef, useState } from "react";
import { Quote } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const testimonials = [{
  id: 1,
  author: "Kate Smith",
  quote: "\"We used Pulse AV for our annual conference. Faultless setup brilliant team and the sound & LED screen quality was outstanding.\"",
  position: "Legal Professional",
  chapter: "LUTON COUNTY COURT",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672919/img/english_lady_lawyer_kate_smith.png"
}, {
  id: 2,
  author: "Jas Kaur",
  quote: "\"The AV production for our wedding was absolutely flawless. Pulse AV brought a level of technical creativity we had never seen before\"",
  position: "Bride",
  chapter: "WEDDING CLIENT",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672919/img/image.png"
}, {
  id: 3,
  author: "Danny & Georgie",
  quote: "\"The PulseFX sparkular machines during our first dance were absolutely stunning. Every single guest had their phone out and the photos are incredible.\"",
  position: "Operations Lead",
  chapter: "WEDDING CLIENT",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672919/img/IMG_5966.jpg"
}];
export default function VoicesOfTheVine() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="221484" id="client-ovations" className="bg-background text-foreground relative overflow-hidden py-12 md:py-20 dark">
      {}
      <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1672919/img/soundwave_pattern_dark.png)] bg-cover bg-center opacity-5 mix-blend-screen pointer-events-none" />

      <div ref={ref} className={`container mx-auto px-4 relative z-10 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="text-center mb-16 md:mb-24">
          <h2 className="text-sm font-mono tracking-widest uppercase text-secondary">Client Testimonials</h2>
        </div>

        <Carousel opts={{
        loop: true,
        align: "center"
      }} className="w-full max-w-5xl mx-auto">
          <CarouselContent>
            {testimonials.map((testimonial, i) => <CarouselItem key={testimonial.id} data-index={i}>
                <div className="flex flex-col items-center text-center px-4 md:px-16 lg:px-24">
                  <Quote className="size-10 md:size-14 text-secondary/30 mb-8 md:mb-12" />
                  
                  <blockquote className="font-serif text-2xl md:text-4xl lg:text-5xl italic leading-relaxed md:leading-tight mb-12 text-foreground/95">
                    "{testimonial.quote}"
                  </blockquote>
                  
                  <div className="flex items-center gap-5">
                    <SafeHydrate><Avatar className="size-16 md:size-20 border-2 border-primary/20 rounded-lg shadow-xl">
                      <AvatarImage src={testimonial.avatar} alt={testimonial.author} />
                      <AvatarFallback className="font-default text-xl bg-card text-primary">
                        {testimonial.author.charAt(0)}
                      </AvatarFallback>
                    </Avatar></SafeHydrate>
                    <div className="text-left">
                      <div className="font-default font-bold text-lg md:text-xl text-foreground tracking-tight">
                        {testimonial.author}
                      </div>
                      <div className="text-sm font-mono text-muted-foreground tracking-wider uppercase mt-1">
                        {testimonial.chapter}
                      </div>
                    </div>
                  </div>
                </div>
              </CarouselItem>)}
          </CarouselContent>
          
          <div className="hidden md:block">
            <CarouselPrevious className="left-0 xl:-left-12 bg-transparent border-border/10 text-muted-foreground hover:bg-primary hover:text-primary-foreground size-12 rounded-md transition-colors" />
            <CarouselNext className="right-0 xl:-right-12 bg-transparent border-border/10 text-muted-foreground hover:bg-primary hover:text-primary-foreground size-12 rounded-md transition-colors" />
          </div>
        </Carousel>
      </div>
    </section>;
}